import * as Sentry from '@sentry/react';
import { maskTokenUrl } from './maskTokenUrl';

/**
 * Frontend Sentry initialiser. Called from `main.tsx` before `createRoot()` so the SDK
 * patches `window.onerror` / `unhandledrejection` and catches early bootstrap errors.
 *
 * No-op when `VITE_SENTRY_DSN` is empty (local dev without a Sentry account) or when the
 * init call itself throws (defensive — Sentry is observability, not a precondition).
 *
 * Returns true if Sentry was initialised, otherwise false.
 */
export function initSentry(): boolean {
  const dsn = (import.meta.env.VITE_SENTRY_DSN ?? '').trim();
  if (!dsn) return false;

  const environment = (import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development') as
    | 'development'
    | 'staging'
    | 'production';
  const tracesSampleRate = clampSample(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1);

  try {
    Sentry.init({
      dsn,
      environment,
      tracesSampleRate,
      // Session replay deferred to Phase 11 — keep both knobs at 0.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Keep PII out at the SDK level; user context is attached explicitly via setUser().
      sendDefaultPii: false,
      beforeSend(event, hint) {
        if (isNoiseEvent(event, hint)) return null;
        return maskEventUrls(event);
      },
      // Recruitment workflow step 5c — token-bearing URLs (invite, reset-password,
      // verify-email) must never reach Sentry: mask them in events, transactions and the
      // navigation / fetch breadcrumbs attached to every event.
      beforeSendTransaction(event) {
        return maskEventUrls(event);
      },
      beforeBreadcrumb(breadcrumb) {
        const data = breadcrumb.data;
        if (data) {
          for (const key of ['url', 'from', 'to']) {
            if (typeof data[key] === 'string') data[key] = maskTokenUrl(data[key] as string);
          }
        }
        if (typeof breadcrumb.message === 'string') breadcrumb.message = maskTokenUrl(breadcrumb.message);
        return breadcrumb;
      },
    });
    return true;
  } catch {
    return false;
  }
}

/** Masks token-bearing URLs on an event's request, transaction name and page URL tags. */
function maskEventUrls<E extends Sentry.Event>(event: E): E {
  if (event.request?.url) event.request.url = maskTokenUrl(event.request.url);
  if (typeof event.transaction === 'string') event.transaction = maskTokenUrl(event.transaction);
  if (event.tags && typeof event.tags.url === 'string') event.tags.url = maskTokenUrl(event.tags.url);
  return event;
}

/**
 * Drop events whose error message matches well-known browser-extension / cross-origin /
 * third-party patterns. These almost always come from the user's own browser, not our
 * code, and only inflate the Sentry quota.
 */
function isNoiseEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  const original = (hint.originalException ?? null) as { message?: unknown } | null;
  const msg =
    (typeof original?.message === 'string' ? original.message : null) ??
    event.message ??
    '';
  const text = typeof msg === 'string' ? msg.toLowerCase() : '';
  if (!text) return false;
  return (
    text.includes('extension://') ||
    text.includes('chrome-extension') ||
    text.includes('moz-extension') ||
    text.includes('safari-extension') ||
    text.includes('script error.') ||
    // ResizeObserver loop quirks — fired by some browsers on legitimate UI work.
    text.includes('resizeobserver loop')
  );
}

function clampSample(raw: string | undefined, fallback: number): number {
  if (raw === undefined) return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
